/**
 * Deterministic Shopify blog-article publish.
 *
 * Calls chatbot-api `POST /integration/shopify/article/publish`, which forwards to
 * yepairag and runs Shopify's `articleCreate` directly — WITHOUT routing through
 * Maya's search→plan→execute tool loop. An approved draft's title, body, image and
 * destination are all fixed by the time the merchant presses the button, so the
 * publish is executed in code instead of gambling on the model emitting the right
 * SHOPIFY_CREATE_ARTICLE call (which can fumble params or never fire, leaving
 * "nothing published, no error").
 *
 * Unlike the social publish path, a failure here does NOT fall back to the agent:
 * a timeout or dropped connection cannot prove the article wasn't created, and
 * re-publishing through Maya would put a second copy on the merchant's blog.
 * Failures are shown instead — see `ArticlePublishError.retryable`.
 */
import { apiClient } from '@lib/core/api';
import {
  isErrorEnvelope,
  scopeErrorFromEnvelope,
  ShopifyScopeError,
} from '@lib/services/shopify/scopeError';

export interface ArticlePublishResult {
  ok: boolean;
  article_id: string;
  /** Live storefront URL; used for the confirmation card's link. */
  article_url?: string;
  blog_id: string;
  is_published: boolean;
  /** Non-fatal degradations, e.g. an over-long summary dropped as meta description. */
  warnings?: string[];
}

/**
 * Backend codes this client keys behavior off. chatbot-api prefixes the message with
 * the code (`NO_BLOG: …`) precisely so they survive the error envelope.
 */
export const ARTICLE_CREATED_NOT_PUBLISHED = 'ARTICLE_CREATED_NOT_PUBLISHED';
/** This draft is already being published elsewhere. Nothing was sent, so a retry is safe. */
export const PUBLISH_ALREADY_RUNNING = 'PUBLISH_ALREADY_RUNNING';

export class ArticlePublishError extends Error {
  /** Backend code when one was carried, else ''. */
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'ArticlePublishError';
    this.code = code;
    Object.setPrototypeOf(this, ArticlePublishError.prototype);
  }

  /**
   * Whether offering the merchant a retry is safe.
   *
   * False for ARTICLE_CREATED_NOT_PUBLISHED: an article already exists in their store,
   * and retrying would stack up duplicates. Also false for an unknown failure — a
   * timeout is indistinguishable from a slow success, so the merchant is told to check
   * their blog rather than handed a button that might double-post.
   */
  get retryable(): boolean {
    return this.code !== '' && this.code !== ARTICLE_CREATED_NOT_PUBLISHED;
  }
}

const CODE_PREFIX = /^([A-Z_]+):\s*(.*)$/s;

/**
 * The api interceptor resolves most non-2xx responses as a {errorCode, errorMessage}
 * envelope with a 200 status instead of throwing (only 401 refreshes / 502 throws).
 * Without this, `.then` would receive the error envelope as a successful publish and
 * the UI would synthesise a "Published" card for an article that does not exist.
 *
 * A missing-scope failure throws the typed ShopifyScopeError instead, so callers can
 * offer the reconnect dialog rather than re-deriving it from the message text.
 */
function unwrap(data: unknown): ArticlePublishResult {
  if (isErrorEnvelope(data)) {
    const scopeError = scopeErrorFromEnvelope(data);
    if (scopeError) throw scopeError;
    const raw = String(data.errorMessage ?? 'Publishing to your Shopify blog failed.');
    const matched = CODE_PREFIX.exec(raw);
    throw new ArticlePublishError(matched ? matched[2] : raw, matched ? matched[1] : '');
  }
  const result = data as ArticlePublishResult | null;
  // An article id is what makes this a publish. A 2xx with nothing in it must not become a
  // "Published" card for an article that does not exist — and it must not offer a retry
  // either, because we cannot tell an empty body from a slow success.
  if (!result?.article_id) {
    throw new ArticlePublishError(
      "We couldn't confirm whether the post went live.",
      ''
    );
  }
  return result;
}

/**
 * Per-draft publish state for this browser session: what is in flight, and what we could not
 * rule out.
 *
 * Module-level rather than component state on purpose. Both are facts about the DRAFT, not
 * about whichever component happened to ask — and both entry points unmount: the Hub's modal
 * disappears when it closes, and Content Studio's page does when the merchant navigates to
 * the Hub. State living inside either would reset at exactly the moment it was still needed,
 * handing them a live Publish button for a post that may already be on their blog.
 *
 * Keys are plural because the same draft is known by different ids in the two places
 * (Content Studio holds the agent's `draft_<hex>`, the Hub the backend row UUID). Rather than
 * inventing one canonical identity, every id we hold is registered and any match counts.
 *
 * Session-scoped by design: the recovery is to look at the Shopify blog, and a page reload is
 * a reasonable place to let the merchant decide again.
 */
// key → the token of the publish holding it. A MAP, not a set: two publishes can be running
// for different drafts, and each must only ever release what it itself reserved.
const inFlightPublishes = new Map<string, string>();
const unresolvedPublishes = new Set<string>();
let publishSeq = 0;

type PublishKey = string | null | undefined;

function present(keys: PublishKey[]): string[] {
  return keys.filter((k): k is string => !!k);
}

/**
 * Reserve a publish for `keys`, or return null when one is already running or unresolved.
 * The returned token owns exactly the keys it reserved.
 *
 * The reservation is what stops a SECOND publish while the first is still in flight — the
 * per-component double-click guards cannot, because navigating from Content Studio to the Hub
 * unmounts one and mounts the other mid-request.
 */
export function beginPublish(keys: PublishKey[]): string | null {
  const ids = present(keys);
  if (ids.some(k => inFlightPublishes.has(k) || unresolvedPublishes.has(k))) return null;
  const token = `publish-${++publishSeq}`;
  ids.forEach(k => inFlightPublishes.set(k, token));
  return token;
}

/**
 * Register further ids for a running publish, or return false when any of them belongs to
 * another publish. The backend row id — learned only after the draft is persisted — is the id
 * the OTHER entry point knows this draft by.
 *
 * Registering has to go through the shared map, not a local array: an id that is only in the
 * caller's list reserves nothing, so the other entry point would happily publish the same
 * draft again.
 *
 * All-or-nothing, and the caller MUST abort on false. Skipping a conflicting id and carrying
 * on would mean the very thing the reservation exists to prevent: this publish proceeding
 * while another one holds the same draft, and the merchant getting two blog posts.
 */
export function addPublishKeys(token: string, keys: PublishKey[]): boolean {
  const ids = present(keys);
  const conflicted = ids.some(k => {
    const owner = inFlightPublishes.get(k);
    return (owner !== undefined && owner !== token) || unresolvedPublishes.has(k);
  });
  if (conflicted) return false;
  ids.forEach(k => inFlightPublishes.set(k, token));
  return true;
}

/**
 * Release the publish `token` holds. `safe` says whether the outcome is known — a success, or
 * a failure that PROVES nothing was written. When it isn't, those ids stay latched for the
 * session.
 *
 * Only this token's own ids move. Releasing by key list instead would let a finishing publish
 * free an id a DIFFERENT publish had since reserved.
 */
export function endPublish(token: string | null, safe: boolean): void {
  if (!token) return;
  for (const [key, owner] of [...inFlightPublishes.entries()]) {
    if (owner !== token) continue;
    inFlightPublishes.delete(key);
    if (!safe) unresolvedPublishes.add(key);
  }
}

/** Whether any of `keys` has a publish in flight or an outcome we could not rule out. */
export function isPublishBlocked(keys: PublishKey[]): boolean {
  return present(keys).some(k => inFlightPublishes.has(k) || unresolvedPublishes.has(k));
}

/** Whether any of `keys` has an outcome we could not rule out (in-flight does not count). */
export function isPublishUnresolved(keys: PublishKey[]): boolean {
  return present(keys).some(k => unresolvedPublishes.has(k));
}

/**
 * Whether retrying after `err` is safe — i.e. whether it PROVES nothing was written.
 *
 * A missing scope counts as safe: Shopify rejected the call before creating anything, and the
 * merchant is about to grant the permission and come straight back. Anything uncoded
 * (timeouts above all) does not, because a timeout is indistinguishable from a slow success.
 */
export function isSafeToRetry(err: unknown): boolean {
  if (err instanceof ShopifyScopeError) return true;
  return err instanceof ArticlePublishError && err.retryable;
}

export interface ArticlePublishParams {
  /** Tenant id — equals the logged-in user's id. */
  tenantId: string;
  title: string;
  /** Draft body as markdown; the backend converts it to the article HTML. */
  bodyMarkdown: string;
  /** Draft excerpt. Doubles as the SEO meta description when it fits. */
  summary?: string;
  tags?: string[];
  /** Durable cover image key (from the draft's media), preferred over imageUrl. */
  imageKey?: string;
  imageUrl?: string;
  /** Blog GID; the backend picks one (news, else by handle) when omitted. */
  blogId?: string;
  /** Backend ContentDraft row id, so the archived publish links to its draft exactly. */
  draftId?: string;
  /** Publish again although already live (contract v1.2 §5.4) — the Published page's Republish only. */
  republish?: boolean;
}

export async function publishArticleDirect(
  params: ArticlePublishParams
): Promise<ArticlePublishResult> {
  const { data } = await apiClient.chatbotPost<unknown>('/integration/shopify/article/publish', {
    tenant_id: params.tenantId,
    title: params.title,
    body_markdown: params.bodyMarkdown,
    summary: params.summary,
    tags: params.tags,
    image_key: params.imageKey,
    image_url: params.imageUrl,
    blog_id: params.blogId,
    draft_id: params.draftId,
    ...(params.republish ? { republish: true } : {}),
  });
  return unwrap(data);
}
